import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

const ThemeProviderInner = ({ children }) => {
  const location = useLocation();
  const [isLightMode, setIsLightMode] = useState(() => {
    // Check localStorage for saved theme preference
    const savedTheme = localStorage.getItem("silverfort-theme");
    if (savedTheme) {
      return savedTheme === "light";
    }
    // Default to dark mode
    return false;
  });

  // Store the user's preferred theme (not including dashboard override)
  const userPreferredTheme = useRef(isLightMode);
  const prevDashboardState = useRef(false);
  const isDashboardPage =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/ar/dashboard");

  // Handle theme based on dashboard state
  useEffect(() => {
    if (isDashboardPage) {
      // When entering dashboard: save current theme and force dark mode
      if (!prevDashboardState.current) {
        userPreferredTheme.current = isLightMode;
      }
      // Force dark mode on dashboard
      document.body.classList.add("dark-mode");
      document.body.classList.remove("light-mode");
    } else {
      // When leaving dashboard: restore user's preferred theme
      if (prevDashboardState.current) {
        setIsLightMode(userPreferredTheme.current);
      }

      // Apply user's preferred theme to document body
      if (isLightMode) {
        document.body.classList.add("light-mode");
        document.body.classList.remove("dark-mode");
      } else {
        document.body.classList.add("dark-mode");
        document.body.classList.remove("light-mode");
      }

      // Save theme preference to localStorage (only when not on dashboard)
      localStorage.setItem("silverfort-theme", isLightMode ? "light" : "dark");
    }

    // Update previous dashboard state
    prevDashboardState.current = isDashboardPage;
  }, [isDashboardPage, isLightMode]);

  const toggleTheme = () => {
    // Only allow theme toggle when not on dashboard
    if (!isDashboardPage) {
      const newMode = !isLightMode;
      setIsLightMode(newMode);
      userPreferredTheme.current = newMode;
    }
  };

  // Calculate effective theme (always dark on dashboard)
  const effectiveLightMode = isDashboardPage ? false : isLightMode;

  const value = {
    isLightMode: effectiveLightMode,
    toggleTheme,
    isDashboard: isDashboardPage,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

// Export ThemeProviderInner as ThemeProvider (since it's now inside Router)
export const ThemeProvider = ThemeProviderInner;
